import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowUpRight, Inbox, Mail, Briefcase, LogOut, Trash2, Check, X, Phone, Building2 } from 'lucide-react';
import {
  adminStats, adminLeads, adminSubscribers, adminApplications,
  adminUpdateLead, adminDeleteLead,
} from '../../lib/api';

const TABS = [
  { id: 'leads', label: 'Leads', icon: Inbox },
  { id: 'subs', label: 'Subscribers', icon: Mail },
  { id: 'apps', label: 'Applications', icon: Briefcase },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('leads');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const [s, l, sb, a] = await Promise.all([adminStats(), adminLeads(), adminSubscribers(), adminApplications()]);
      setStats(s); setLeads(l); setSubs(sb); setApps(a);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem('bs_admin_token');
    navigate('/admin/login');
  };

  const updateStatus = async (id, status) => {
    await adminUpdateLead(id, status);
    await load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };
  const removeLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await adminDeleteLead(id);
    if (selected?.id === id) setSelected(null);
    await load();
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[var(--navy)] text-white flex flex-col">
        <Link to="/" className="flex items-center gap-3 p-6 border-b border-white/10">
          <div className="relative">
            <span className="font-display text-3xl leading-none">B</span>
            <span className="absolute -right-1 bottom-1 w-2 h-2 rounded-full bg-[var(--coral)]" />
          </div>
          <div>
            <div className="font-sans-display text-sm font-semibold">Bespoke</div>
            <div className="font-tag opacity-70 text-[10px]">Admin</div>
          </div>
        </Link>
        <nav className="flex-1 p-4 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); setSelected(null); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-sm transition-colors ${tab === t.id ? 'bg-white/10 text-[var(--coral)]' : 'hover:bg-white/5'}`}>
              <t.icon size={16} />
              {t.label}
              {t.id === 'leads' && stats && <span className="ml-auto text-[10px] bg-[var(--coral)] text-white rounded-full px-2 py-0.5">{stats.leads_new}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm hover:bg-white/5 transition-colors"><LogOut size={16} /> Sign out</button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-[var(--line)] px-8 py-6 flex items-center justify-between">
          <div>
            <div className="font-tag text-[var(--ink-mute)]">Studio dashboard</div>
            <h1 className="font-display text-3xl mt-1">{TABS.find((t) => t.id === tab)?.label}</h1>
          </div>
          <Link to="/" className="font-tag u-link">View site —</Link>
        </header>

        {/* Stats */}
        {stats && (
          <div className="px-8 py-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Total Leads" value={stats.leads_total} />
            <StatCard label="New" value={stats.leads_new} accent />
            <StatCard label="Won" value={stats.leads_won} />
            <StatCard label="Subscribers" value={stats.subscribers} />
            <StatCard label="Applications" value={stats.applications} />
          </div>
        )}

        <div className="px-8 pb-12">
          {tab === 'leads' && (
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7">
                <div className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--bg-soft)]">
                      <tr>
                        <Th>Name</Th><Th>Service</Th><Th>Budget</Th><Th>Status</Th><Th>Date</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} onClick={() => setSelected(l)} className={`border-t border-[var(--line)] cursor-pointer hover:bg-[var(--bg-soft)] ${selected?.id === l.id ? 'bg-[var(--bg-soft)]' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{l.name}</div>
                            <div className="text-[12px] text-[var(--ink-mute)]">{l.email}</div>
                          </td>
                          <td className="px-4 py-3">{l.service || '—'}</td>
                          <td className="px-4 py-3">{l.budget || '—'}</td>
                          <td className="px-4 py-3"><StatusPill s={l.status} /></td>
                          <td className="px-4 py-3 text-[12px] text-[var(--ink-mute)]">{new Date(l.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                      ))}
                      {leads.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-[var(--ink-mute)]">No leads yet — submit one from the public Contact page.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="lg:col-span-5">
                {selected ? (
                  <div className="bg-white border border-[var(--line)] rounded-2xl p-6 sticky top-6">
                    <div className="flex items-center justify-between">
                      <StatusPill s={selected.status} />
                      <button onClick={() => removeLead(selected.id)} className="w-9 h-9 rounded-full hover:bg-red-50 hover:text-red-600 flex items-center justify-center text-[var(--ink-mute)]" aria-label="Delete"><Trash2 size={14} /></button>
                    </div>
                    <div className="font-display text-3xl mt-4">{selected.name}</div>
                    {selected.company && <div className="font-tag text-[var(--ink-mute)] mt-1 flex items-center gap-2"><Building2 size={12} /> {selected.company}</div>}
                    <div className="mt-5 space-y-2 text-sm">
                      <a href={`mailto:${selected.email}`} className="flex items-center gap-3 u-link"><Mail size={14} /> {selected.email}</a>
                      {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-3 u-link"><Phone size={14} /> {selected.phone}</a>}
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-4">
                      <Info l="Service" v={selected.service || '—'} />
                      <Info l="Budget" v={selected.budget || '—'} />
                    </div>
                    <div className="mt-5">
                      <div className="font-tag text-[var(--ink-mute)] mb-2">Message</div>
                      <p className="text-sm leading-relaxed">{selected.message}</p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-[var(--line)]">
                      <div className="font-tag text-[var(--ink-mute)] mb-3">Update status</div>
                      <div className="flex flex-wrap gap-2">
                        {['new', 'contacted', 'won', 'lost'].map((s) => (
                          <button key={s} onClick={() => updateStatus(selected.id, s)} className={`px-4 py-2 rounded-full font-tag border transition-colors ${selected.status === s ? 'bg-[var(--navy)] text-white border-[var(--navy)]' : 'border-[var(--line)] hover:border-[var(--navy)]'}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-dashed border-[var(--line)] rounded-2xl p-10 text-center text-[var(--ink-mute)] font-tag">Select a lead to view details</div>
                )}
              </div>
            </div>
          )}

          {tab === 'subs' && (
            <div className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-soft)]"><tr><Th>Email</Th><Th>Date</Th></tr></thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t border-[var(--line)]">
                      <td className="px-4 py-3">{s.email}</td>
                      <td className="px-4 py-3 text-[12px] text-[var(--ink-mute)]">{new Date(s.created_at).toLocaleString('en-GB')}</td>
                    </tr>
                  ))}
                  {subs.length === 0 && <tr><td colSpan={2} className="px-4 py-12 text-center text-[var(--ink-mute)]">No subscribers yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'apps' && (
            <div className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-soft)]"><tr><Th>Name</Th><Th>Role</Th><Th>Email</Th><Th>Date</Th></tr></thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} className="border-t border-[var(--line)]">
                      <td className="px-4 py-3 font-medium">{a.name}</td>
                      <td className="px-4 py-3">{a.role || '—'}</td>
                      <td className="px-4 py-3"><a href={`mailto:${a.email}`} className="u-link">{a.email}</a></td>
                      <td className="px-4 py-3 text-[12px] text-[var(--ink-mute)]">{new Date(a.created_at).toLocaleString('en-GB')}</td>
                    </tr>
                  ))}
                  {apps.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-[var(--ink-mute)]">No applications yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`p-5 rounded-2xl border ${accent ? 'bg-[var(--navy)] text-white border-[var(--navy)]' : 'bg-white border-[var(--line)]'}`}>
      <div className={`font-tag ${accent ? 'opacity-70' : 'text-[var(--ink-mute)]'}`}>{label}</div>
      <div className="font-display text-4xl mt-1">{value}{accent && <span className="text-[var(--coral)]">.</span>}</div>
    </div>
  );
}
function Th({ children }) { return <th className="text-left px-4 py-3 font-tag text-[var(--ink-mute)]">{children}</th>; }
function Info({ l, v }) {
  return (
    <div>
      <div className="font-tag text-[var(--ink-mute)]">{l}</div>
      <div className="mt-1 font-medium">{v}</div>
    </div>
  );
}
function StatusPill({ s }) {
  const map = {
    new: 'bg-[var(--coral)]/10 text-[var(--coral)]',
    contacted: 'bg-blue-100 text-blue-700',
    won: 'bg-green-100 text-green-700',
    lost: 'bg-gray-100 text-gray-600',
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-medium ${map[s] || ''}`}>{s}</span>;
}
